/**
 * IDScanner - Patient ID / Insurance Card Photo Scanner with OCR
 * 
 * Captures a photo of a patient's ID or insurance card
 * and extracts information using OpenAI Vision API.
 * 
 * Modes:
 * - 'id' (default): Extracts name and date of birth from government ID
 * - 'insurance': Extracts plan name, member ID, group number from insurance card
 * 
 * Features:
 * - Camera capture with preview
 * - OCR extraction via Firebase function
 * - Confirmation dialog to verify/edit extracted data
 * - Returns captured image base64 alongside extracted data
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  Paper,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  ErrorOutline,
  Refresh,
  PhotoCamera,
  Edit,
  Badge,
  CreditCard,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseApp } from '@healthdesk/shared-services';

const functions = getFunctions(firebaseApp);
const extractPatientID = httpsCallable(functions, 'chartmindExtractPatientID');
const extractInsuranceCard = httpsCallable(functions, 'chartmindExtractInsuranceCard');

/**
 * IDScanner Component
 * 
 * @param {function} onPatientExtracted - Called with extracted data when confirmed (works for both modes)
 * @param {function} onClose - Called when scanner is closed
 * @param {boolean} open - Whether the scanner dialog is open
 * @param {'id'|'insurance'} mode - Scan mode: 'id' for photo ID, 'insurance' for insurance card
 */
const IDScanner = ({ onPatientExtracted, onClose, open = false, mode = 'id' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const isInsurance = mode === 'insurance';
  
  // Scanner state
  const [step, setStep] = useState('camera'); // 'camera' | 'processing' | 'confirm' | 'error'
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [capturedImageBase64, setCapturedImageBase64] = useState(null);
  
  // Extraction state
  const [_extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  
  // Editable fields for ID mode confirmation
  const [editName, setEditName] = useState('');
  const [editDob, setEditDob] = useState('');
  
  // Editable fields for insurance mode confirmation
  const [editPlanName, setEditPlanName] = useState('');
  const [editMemberId, setEditMemberId] = useState('');
  const [editGroupNumber, setEditGroupNumber] = useState('');
  const [editInsuranceCompany, setEditInsuranceCompany] = useState('');
  const [editPolicyHolderName, setEditPolicyHolderName] = useState('');

  // Start camera when dialog opens
  useEffect(() => {
    if (open && step === 'camera') {
      startCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [open, step]);

  const startCamera = async () => {
    setCameraError(null);
    setCameraReady(false);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('[IDScanner] Camera error:', err);
      
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera access.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Failed to access camera. Please try again.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  };

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    const base64Image = imageDataUrl.split(',')[1];
    
    setCapturedImageBase64(base64Image);
    stopCamera();
    processImage(base64Image);
  }, [isInsurance]);

  const processImage = async (base64Image) => {
    setStep('processing');
    setExtracting(true);
    setExtractionError(null);
    
    try {
      const extractFn = isInsurance ? extractInsuranceCard : extractPatientID;
      const result = await extractFn({ imageBase64: base64Image });
      const data = result.data;
      
      if (isInsurance) {
        if (!data.planName && !data.memberId && !data.insuranceCompany) {
          throw new Error('Could not extract insurance information from the image. Please try again with a clearer photo.');
        }
        setExtractedData(data);
        setEditPlanName(data.planName || '');
        setEditMemberId(data.memberId || '');
        setEditGroupNumber(data.groupNumber || '');
        setEditInsuranceCompany(data.insuranceCompany || '');
        setEditPolicyHolderName(data.policyHolderName || '');
      } else {
        if (!data.name && !data.dob) {
          throw new Error('Could not extract patient information from the image. Please try again with a clearer photo.');
        }
        setExtractedData(data);
        setEditName(data.name || '');
        setEditDob(data.dob || '');
      }
      
      setStep('confirm');
    } catch (err) {
      console.error('[IDScanner] Extraction error:', err);
      setExtractionError(err.message || 'Failed to extract information');
      setStep('error');
    } finally {
      setExtracting(false);
    }
  };

  const handleRetry = () => {
    setCapturedImageBase64(null);
    setExtractedData(null);
    setExtractionError(null);
    setStep('camera');
  };

  const handleConfirm = () => {
    if (isInsurance) {
      if (!editInsuranceCompany.trim() && !editPlanName.trim()) return;
      
      const insuranceData = {
        planName: editPlanName.trim(),
        memberId: editMemberId.trim(),
        groupNumber: editGroupNumber.trim(),
        insuranceCompany: editInsuranceCompany.trim(),
        policyHolderName: editPolicyHolderName.trim(),
        source: 'insurance_scan',
        confidence: extractedData?.confidence || 'manual',
        imageBase64: capturedImageBase64,
      };
      onPatientExtracted?.(insuranceData);
    } else {
      if (!editName.trim()) return;
      
      const patientData = {
        name: editName.trim(),
        dob: editDob || null,
        source: 'id_scan',
        confidence: extractedData?.confidence || 'manual',
        imageBase64: capturedImageBase64,
      };
      onPatientExtracted?.(patientData);
    }
    handleClose();
  };

  const handleClose = () => {
    stopCamera();
    setCapturedImageBase64(null);
    setExtractedData(null);
    setExtractionError(null);
    setStep('camera');
    setEditName('');
    setEditDob('');
    setEditPlanName('');
    setEditMemberId('');
    setEditGroupNumber('');
    setEditInsuranceCompany('');
    setEditPolicyHolderName('');
    onClose?.();
  };

  const renderCamera = () => (
    <Box>
      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          maxWidth: 500,
          mx: 'auto',
          aspectRatio: '4/3',
          bgcolor: 'black',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          playsInline
          muted
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            right: '5%',
            bottom: '10%',
            border: '2px dashed rgba(255,255,255,0.6)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {!cameraReady && !cameraError && (
            <CircularProgress sx={{ color: 'white' }} />
          )}
        </Box>
      </Box>
      
      <Typography 
        variant="body2" 
        color="text.secondary" 
        align="center"
        sx={{ mt: 2, mb: 1 }}
      >
        {isInsurance 
          ? 'Position the insurance card within the frame' 
          : "Position the patient's ID within the frame"}
      </Typography>
      
      {cameraError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {cameraError}
          <Button size="small" onClick={startCamera} sx={{ ml: 1 }}>
            Try Again
          </Button>
        </Alert>
      )}
      
      {cameraReady && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PhotoCamera />}
            onClick={capturePhoto}
            sx={{ borderRadius: 3, px: 4, py: 1.5 }}
          >
            Capture Photo
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderProcessing = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={64} sx={{ mb: 3 }} />
      <Typography variant="h6" gutterBottom>
        {isInsurance ? 'Extracting Insurance Information...' : 'Extracting Patient Information...'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Analyzing the image using AI
      </Typography>
    </Box>
  );

  const renderConfirmID = () => (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="h6">Patient Information Extracted</Typography>
        <Typography variant="body2" color="text.secondary">
          Please verify and edit if needed
        </Typography>
        {extractedData?.confidence && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: extractedData.confidence === 'high' ? 'success.main' : 
                     extractedData.confidence === 'medium' ? 'warning.main' : 'error.main',
              fontWeight: 500,
            }}
          >
            Confidence: {extractedData.confidence}
          </Typography>
        )}
      </Box>
      
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <TextField
          fullWidth
          label="Patient Name"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          required
          error={!editName.trim()}
          helperText={!editName.trim() ? 'Name is required' : ''}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <Edit sx={{ mr: 1, color: 'action.active' }} />,
          }}
        />
        <TextField
          fullWidth
          label="Date of Birth"
          type="date"
          value={editDob}
          onChange={(e) => setEditDob(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Format: YYYY-MM-DD"
        />
      </Paper>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        This information was extracted from a photo and should be verified.
      </Alert>
    </Box>
  );

  const renderConfirmInsurance = () => (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="h6">Insurance Information Extracted</Typography>
        <Typography variant="body2" color="text.secondary">
          Please verify and edit if needed
        </Typography>
        {extractedData?.confidence && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: extractedData.confidence === 'high' ? 'success.main' : 
                     extractedData.confidence === 'medium' ? 'warning.main' : 'error.main',
              fontWeight: 500,
            }}
          >
            Confidence: {extractedData.confidence}
          </Typography>
        )}
      </Box>
      
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <TextField
          fullWidth
          label="Insurance Company"
          value={editInsuranceCompany}
          onChange={(e) => setEditInsuranceCompany(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <Edit sx={{ mr: 1, color: 'action.active' }} />,
          }}
        />
        <TextField
          fullWidth
          label="Plan Name"
          value={editPlanName}
          onChange={(e) => setEditPlanName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Member ID"
          value={editMemberId}
          onChange={(e) => setEditMemberId(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Group Number"
          value={editGroupNumber}
          onChange={(e) => setEditGroupNumber(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Policy Holder Name"
          value={editPolicyHolderName}
          onChange={(e) => setEditPolicyHolderName(e.target.value)}
        />
      </Paper>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        This information was extracted from a photo and should be verified.
      </Alert>
    </Box>
  );

  const renderError = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <ErrorOutline sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
      <Typography variant="h6" color="error" gutterBottom>
        Extraction Failed
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {extractionError || 'Could not extract information from the image.'}
      </Typography>
      <Button
        variant="contained"
        startIcon={<Refresh />}
        onClick={handleRetry}
      >
        Try Again
      </Button>
    </Box>
  );

  const confirmDisabled = isInsurance
    ? (!editInsuranceCompany.trim() && !editPlanName.trim())
    : !editName.trim();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isInsurance ? <CreditCard /> : <Badge />}
            <Typography variant="h6">
              {isInsurance ? 'Scan Insurance Card' : 'Scan Patient ID'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {step === 'camera' && renderCamera()}
        {step === 'processing' && renderProcessing()}
        {step === 'confirm' && (isInsurance ? renderConfirmInsurance() : renderConfirmID())}
        {step === 'error' && renderError()}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'confirm' && (
          <>
            <Button onClick={handleRetry}>
              Retake Photo
            </Button>
            <Button 
              variant="contained" 
              onClick={handleConfirm}
              disabled={confirmDisabled}
            >
              Confirm & Use
            </Button>
          </>
        )}
        {(step === 'camera' || step === 'error') && (
          <Button onClick={handleClose}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IDScanner;
